import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { DemoBadge, Empty, ErrorBox, Field, PageHead, Spinner, StatusBadge, useFetch } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const STATUSES = ["NOT_SUBMITTED", "PENDING", "CONFIRMED", "REJECTED", "EXPIRED"];

function EditEnrollment({ enr, onDone }) {
  const [f, setF] = useState({ status: enr.status, policy_reference: enr.policy_reference || "", coverage_start: enr.coverage_start || "",
    coverage_end: enr.coverage_end || "", coverage_details: enr.coverage_details || "", documents: enr.documents || [], verification_note: enr.verification_note || "" });
  const [doc, setDoc] = useState({ name: "", reference: "" });
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    const nul = (v) => v || null;
    try {
      await api.put(`/admin/insurance/${enr.id}`, { ...f, policy_reference: nul(f.policy_reference), coverage_start: nul(f.coverage_start), coverage_end: nul(f.coverage_end),
        coverage_details: nul(f.coverage_details), verification_note: nul(f.verification_note) });
      toast.success("Enrollment updated");
      onDone();
    } catch (err) { setError(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className="space-y-3" data-testid="insurance-form">
      <Field label="Enrollment status">
        <select className="field" value={f.status} onChange={set("status")} data-testid="insurance-status-select">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
      </Field>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Policy reference"><input className="field font-mono" value={f.policy_reference} onChange={set("policy_reference")} data-testid="insurance-policy-input" /></Field>
        <Field label="Coverage start"><input className="field" type="date" value={f.coverage_start} onChange={set("coverage_start")} data-testid="insurance-start-input" /></Field>
        <Field label="Coverage end"><input className="field" type="date" value={f.coverage_end} onChange={set("coverage_end")} data-testid="insurance-end-input" /></Field>
      </div>
      <Field label="Coverage details"><textarea className="field" rows={2} value={f.coverage_details} onChange={set("coverage_details")} data-testid="insurance-details-input" /></Field>
      <Field label="Supporting insurer documents (reference / file location)">
        <div className="space-y-2">
          {f.documents.map((d, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-xs"><span><b>{d.name}</b> · {d.reference}</span>
              <button type="button" onClick={() => setF({ ...f, documents: f.documents.filter((_, j) => j !== i) })}><Trash2 size={13} /></button></div>
          ))}
          <div className="flex gap-2">
            <input className="field" placeholder="Document name" value={doc.name} onChange={(e) => setDoc({ ...doc, name: e.target.value })} data-testid="insurance-doc-name-input" />
            <input className="field" placeholder="Reference no. / link" value={doc.reference} onChange={(e) => setDoc({ ...doc, reference: e.target.value })} data-testid="insurance-doc-ref-input" />
            <button type="button" className="btn-secondary" disabled={!doc.name || !doc.reference} onClick={() => { setF({ ...f, documents: [...f.documents, doc] }); setDoc({ name: "", reference: "" }); }} data-testid="insurance-add-doc"><Plus size={14} /></button>
          </div>
        </div>
      </Field>
      <Field label="Verification note" hint="Required to confirm: who at the insurer verified, and how."><textarea className="field" rows={2} value={f.verification_note} onChange={set("verification_note")} data-testid="insurance-note-input" /></Field>
      <ErrorBox error={error} />
      <button className="btn-primary w-full" data-testid="insurance-save-button">Save</button>
    </form>
  );
}

export default function Insurance() {
  const { data, loading, reload } = useFetch("/admin/insurance");
  const [editing, setEditing] = useState(null);
  if (loading && !data) return <Spinner />;
  return (
    <div>
      <PageHead eyebrow="Insurance enrollment tracking" title="Insurance enrollments" sub="Package activation never activates coverage. Confirm only with insurer documentation." />
      {data?.length === 0 ? <Empty title="No enrollments yet" hint="Enrollments are created when a package with an insurance benefit is activated." /> : (
        <div className="surface overflow-x-auto">
          <table className="data-table" data-testid="insurance-table">
            <thead><tr><th>Insured member</th><th>Insurer</th><th>Policy</th><th>Coverage</th><th>Status</th><th /></tr></thead>
            <tbody>{data?.map((e) => (
              <tr key={e.id} data-testid={`insurance-row-${e.id}`}>
                <td className="font-semibold">{e.insured_member} {e.is_demo && <DemoBadge />}</td><td>{e.insurer_name}</td>
                <td className="font-mono text-xs">{e.policy_reference || "—"}</td><td className="text-xs">{e.coverage_start ? `${fmtDate(e.coverage_start)} – ${fmtDate(e.coverage_end)}` : "—"}</td>
                <td><StatusBadge status={e.status} /></td>
                <td><button className="btn-ghost btn-sm" onClick={() => setEditing(e)} data-testid={`insurance-edit-${e.id}`}>Update</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto rounded-3xl">
          <DialogHeader><DialogTitle>Update enrollment</DialogTitle><DialogDescription>{editing?.insured_member} · {editing?.insurer_name}</DialogDescription></DialogHeader>
          {editing && <EditEnrollment enr={editing} onDone={() => { setEditing(null); reload(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
